import { useEffect, useRef } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { tableKeymap } from '../editor/table';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { yCollab } from 'y-codemirror.next';
import { markbirdEditorTheme } from '../editor/theme';
import { COLLAB_URL, type CollabUser } from '../lib/collab';

interface Props {
  /** Room = the share token; everyone on the same token co-edits. */
  token: string;
  /** Seed content when the room is empty (from the Firestore snapshot). */
  initialContent: string;
  user: CollabUser;
  /** Latest merged text, for debounced autosave to Firestore. */
  onTextChange: (text: string) => void;
  /** Number of connected peers (including self). */
  onPeers?: (peers: number) => void;
  /** Connected participants (name + color) for a presence display. */
  onPresence?: (users: CollabUser[]) => void;
}

/**
 * Real-time collaborative markdown editor: a CodeMirror view bound to a shared
 * Yjs document over the collab WebSocket relay, with live remote cursors and
 * presence (y-codemirror.next).
 */
export function CollabEditor({
  token,
  initialContent,
  user,
  onTextChange,
  onPeers,
  onPresence,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hostRef.current || !COLLAB_URL) return;
    const ydoc = new Y.Doc();
    const ytext = ydoc.getText('content');
    const provider = new WebsocketProvider(COLLAB_URL, token, ydoc);
    provider.awareness.setLocalStateField('user', {
      name: user.name,
      color: user.color,
      colorLight: `${user.color}33`,
    });
    const undoManager = new Y.UndoManager(ytext);

    // Seed the room from the snapshot only if it's still empty after sync.
    provider.once('sync', (isSynced: boolean) => {
      if (isSynced && ytext.length === 0 && initialContent) ytext.insert(0, initialContent);
    });

    const mirror = () => onTextChange(ytext.toString());
    ytext.observe(mirror);

    const reportPeers = () => {
      const states = [...provider.awareness.getStates().values()];
      onPeers?.(states.length);
      onPresence?.(
        states
          .map((s) => (s as { user?: CollabUser }).user)
          .filter((u): u is CollabUser => !!u && typeof u.name === 'string'),
      );
    };
    provider.awareness.on('change', reportPeers);
    provider.on('status', reportPeers);

    const view = new EditorView({
      state: EditorState.create({
        doc: ytext.toString(),
        extensions: [
          EditorView.lineWrapping,
          history(),
          keymap.of(tableKeymap),
          keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
          markdown({ base: markdownLanguage, codeLanguages: languages }),
          yCollab(ytext, provider.awareness, { undoManager }),
          markbirdEditorTheme,
        ],
      }),
      parent: hostRef.current,
    });

    return () => {
      ytext.unobserve(mirror);
      provider.awareness.off('change', reportPeers);
      provider.off('status', reportPeers);
      view.destroy();
      provider.destroy();
      ydoc.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return <div ref={hostRef} className="mb-share-cm mb-editor-host" />;
}
